import { Component, OnDestroy, Optional } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { GeneralParameterEnums } from 'src/app/enum/general-parameter-enums';
import { ModuleModel } from 'src/app/models/authentication/module.model';
import { ProfileUserModel } from 'src/app/models/authentication/profile-user.model';
import { WindowProfileUserModel } from 'src/app/models/authentication/window-profile-user.model';
import { WindowModel } from 'src/app/models/authentication/window.model';
import { ModuleService } from 'src/app/services/authentication/module.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { GeneralParameterService } from 'src/app/services/management/general-parameter.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
type ButtonSeverity = 'success' | 'primary' | 'secondary' | 'info' | 'warn' | 'danger' | 'help' | 'contrast' | null | undefined;
type ButtonVariant = 'outlined' | 'text' | undefined;

@Component({
  selector: 'app-permissions-profile-user',
  templateUrl: './permissions-profile-user.component.html',
  styleUrls: ['./permissions-profile-user.component.scss'],
  standalone: false
})

export class PermissionsProfileUserComponent implements OnDestroy {
  appModules: ModuleModel[] = [];
  modules: ModuleModel[] = [];
  profileUserContract: ProfileUserModel = new ProfileUserModel();
  permissionUpdatable: boolean = false;
  subscription: Subscription;
  swAppMovil: boolean = false;
  webModules: ModuleModel[] = [];
  windows: WindowModel[] = [];
  modulesProfileUserId: number[] = [];
  windowsProfileUser: WindowProfileUserModel[] = [];

  constructor(public config: DynamicDialogConfig,
    private confirmDialogService: ConfirmDialogService,
    private generalParameterService: GeneralParameterService,
    private moduleService: ModuleService,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService,
    @Optional() private ref: DynamicDialogRef) { }

  async ngOnInit(): Promise<void> {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.profileUserContract = inputs.profileUserModel;
      this.permissionUpdatable = inputs.permissionUpdatable;
    });

    await this.getGeneralParametersByCompany();
    if (this.profileUserContract.profileUserId != 0) {
      await this.getAllLists();
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  private addNewWindow(windowId: number, value: boolean) {
    const newItem = new WindowProfileUserModel();

    newItem.profileUserId = this.profileUserContract.profileUserId;
    newItem.windowId = windowId;
    newItem.updatable = value;

    this.windowsProfileUser.push(newItem);

    // Asegurar módulo asociado
    const window = this.windows.find(w => w.windowId === windowId);
    if (window && !this.modulesProfileUserId.includes(window.moduleId)) {
      this.modulesProfileUserId.push(window.moduleId);
    }
  }

  changeWindow(value: boolean | null, windowId: number) {
    if (!this.validations()) return;

    const existing = this.windowsProfileUser.find(w => w.windowId === windowId);

    // Si ya existe en la lista
    if (existing) {
      this.updateExistingWindow(existing, value);
    }

    // Si NO existe y debe agregarse 
    else if (value !== null) {
      this.addNewWindow(windowId, value);
    }
  }

  async consultAll(module: ModuleModel) {
    if (!this.validations()) return;
    if (!await this.confirmDialogService.confirm(
      `¿Realmente desea asignar el permiso de Consulta a todas las ventanas del modulo ${module.name}?`
    )) return;
    for (const win of module.windows) {
      const windowProfileUser = this.windowsProfileUser.find(w => w.windowId === win.windowId);

      if (windowProfileUser) {
        // Solo actualiza
        windowProfileUser.updatable = false;
      } else {
        // Crea uno nuevo
        this.windowsProfileUser.push({
          profileUserId: this.profileUserContract.profileUserId,
          windowId: win.windowId,
          updatable: false
        } as WindowProfileUserModel);
      }

      // Asegurar que el módulo se encuentre en la lista
      if (!this.modulesProfileUserId.includes(module.moduleId)) {
        this.modulesProfileUserId.push(module.moduleId);
      }
    }
  }

  async getAllLists() {
    try {
      const [webModules, appModules, windows, modulesProfileUser, windowsProfileUser] = await Promise.all([
        firstValueFrom(this.moduleService.getMenuByCompanyAuthenticatedByPlatform(true)),
        this.swAppMovil
          ? firstValueFrom(this.moduleService.getMenuByCompanyAuthenticatedByPlatform(false))
          : Promise.resolve<ModuleModel[]>([]),
        firstValueFrom(this.windowService.getWindowsByCompanyAuthenticated()),
        firstValueFrom(this.moduleService.getMenuByProfileUser(this.profileUserContract.profileUserId)),
        firstValueFrom(this.windowService.getWindowsByProfileUser(this.profileUserContract.profileUserId))
      ]);
      this.webModules = webModules;
      this.appModules = appModules;
      this.modules = [...webModules, ...appModules];
      this.windows = windows;
      this.modulesProfileUserId = modulesProfileUser.map(m => m.moduleId);
      this.windowsProfileUser = windowsProfileUser;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  async getGeneralParametersByCompany() {
    try {
      const generalParameters = await firstValueFrom(this.generalParameterService.getGeneralParametersByUserAutheticated());
      this.swAppMovil = generalParameters.some(x => x.generalParameterId === GeneralParameterEnums.appMovil);
    } catch (error) {
      console.error('Error al obtener los parámetros generales.', error);
    }
  }

  getTagModule(module: ModuleModel) {
    const moduleEnable = this.modulesProfileUserId.indexOf(module.moduleId) != -1
    return moduleEnable ? 'success' : 'danger';
  }

  getTagTextModule(module: ModuleModel) {
    const moduleEnable = this.modulesProfileUserId.indexOf(module.moduleId) != -1
    return moduleEnable ? 'Habilitado' : 'Inhabilitado';
  }

  private getModuleState(module: ModuleModel) {
    const windowsModule = module.windows.filter(w => w.moduleId === module.moduleId);
    const windowsIds = windowsModule.map(w => w.windowId);

    const windowsUser = this.windowsProfileUser.filter(w => windowsIds.includes(w.windowId));

    const enabled = windowsUser.filter(w => w.updatable).length;
    const disabled = windowsUser.filter(w => !w.updatable).length;
    const total = windowsModule.length;

    let state: 'none' | 'allModify' | 'allConsult' | 'mixed';

    if (total === 0 || (enabled === 0 && disabled === 0)) {
      state = 'none';
    } else if (enabled === total) {
      state = 'allModify';
    } else if (disabled === total) {
      state = 'allConsult';
    } else {
      state = 'mixed';
    }
    return { enabled, disabled, total, state };
  }

  getSeverityModule(module: ModuleModel, typeButton: string): ButtonSeverity {
    const { state } = this.getModuleState(module);

    const severityMap: Record<
      'none' | 'allModify' | 'allConsult' | 'mixed',
      Record<string, ButtonSeverity>
    > = {
      none: {
        restrict: 'danger',
        modify: 'contrast',
        consult: 'contrast',
        default: 'contrast',
      },
      allModify: {
        restrict: 'contrast',
        modify: 'primary',
        consult: 'contrast',
        default: 'contrast',
      },
      allConsult: {
        restrict: 'contrast',
        modify: 'contrast',
        consult: 'success',
        default: 'contrast',
      },
      mixed: {
        restrict: 'contrast',
        modify: 'contrast',
        consult: 'contrast',
        default: 'contrast',
      }
    };
    return severityMap[state][typeButton] ?? severityMap[state].default;
  }


  getVariantModule(module: ModuleModel, typeButton: string): ButtonVariant {
    const { state } = this.getModuleState(module);

    if (state === 'none') {
      return typeButton === 'restrict' ? undefined : 'outlined';
    }

    if (state === 'allModify' && typeButton === 'modify') {
      return undefined;
    }

    if (state === 'allConsult' && typeButton === 'consult') {
      return undefined;
    }
    return 'outlined';
  }


  private getWindowState(window: WindowModel) {
    const winUser = this.windowsProfileUser.find(w => w.windowId === window.windowId);

    let state: 'none' | 'modify' | 'consult';

    if (!winUser) {
      state = 'none';
    } else if (winUser.updatable) {
      state = 'modify';
    } else {
      state = 'consult';
    }
    return { winUser, state };
  }

  getSeverityWindow(window: WindowModel, typeButton: string): ButtonSeverity {
    const { state } = this.getWindowState(window);

    const severityMap: Record<'none' | 'modify' | 'consult', Record<string, ButtonSeverity>> = {
      none: {
        restrict: 'danger',
        modify: 'contrast',
        consult: 'contrast',
        default: 'contrast'
      },
      modify: {
        restrict: 'contrast',
        modify: 'primary',
        consult: 'contrast',
        default: 'contrast'
      },
      consult: {
        restrict: 'contrast',
        modify: 'contrast',
        consult: 'success',
        default: 'contrast'
      }
    };
    return severityMap[state][typeButton] ?? severityMap[state].default;
  }

  getVariantWindow(window: WindowModel, typeButton: string): ButtonVariant {
    const { state } = this.getWindowState(window);

    if (state === 'none') {
      return typeButton === 'restrict' ? undefined : 'outlined';
    }

    if (state === 'modify') {
      return typeButton === 'modify' ? undefined : 'outlined';
    }

    if (state === 'consult') {
      return typeButton === 'consult' ? undefined : 'outlined';
    }
    return 'outlined';
  }

  async modifyAll(module: ModuleModel) {
    if (!this.validations()) return;
    if (!await this.confirmDialogService.confirm(
      `¿Realmente desea asignar el permiso de Modificación a todas las ventanas del modulo ${module.name}?`
    )) return;
    for (const win of module.windows) {
      const windowProfileUser = this.windowsProfileUser.find(w => w.windowId === win.windowId);

      if (windowProfileUser) {
        // Solo actualiza
        windowProfileUser.updatable = true;
      } else {
        // Crea uno nuevo
        this.windowsProfileUser.push({
          profileUserId: this.profileUserContract.profileUserId,
          windowId: win.windowId,
          updatable: true
        } as WindowProfileUserModel);
      }

      // Asegurar que el módulo se encuentre en la lista
      if (!this.modulesProfileUserId.includes(module.moduleId)) {
        this.modulesProfileUserId.push(module.moduleId);
      }
    }
  }

  private removeModuleIfEmpty(windowId: number) {
    const window = this.windows.find(w => w.windowId === windowId);
    if (!window) return;

    const module = this.modules.find(m => m.moduleId === window.moduleId);
    if (!module) return;

    const moduleWindowIds = module.windows.map(w => w.windowId);

    const hasAnyWindow = this.windowsProfileUser.some(w =>
      moduleWindowIds.includes(w.windowId)
    );

    if (!hasAnyWindow) {
      const modIndex = this.modulesProfileUserId.indexOf(module.moduleId);
      if (modIndex !== -1) {
        this.modulesProfileUserId.splice(modIndex, 1);
      }
    }
  }

  async restrictAll(module: ModuleModel) {
    if (!this.validations()) return;
    if (!await this.confirmDialogService.confirm(
      `¿Realmente desea asignar el permiso de Restringir a todas las ventanas del modulo ${module.name}?`
    )) return;
    // Eliminar todas las ventanas de este módulo
    for (const win of module.windows) {
      const index = this.windowsProfileUser.findIndex(w => w.windowId === win.windowId);
      if (index !== -1) {
        this.windowsProfileUser.splice(index, 1);
      }
    }

    // Si el módulo estaba asignado, se quita
    const indexModule = this.modulesProfileUserId.indexOf(module.moduleId);
    if (indexModule !== -1) {
      this.modulesProfileUserId.splice(indexModule, 1);
    }
  }

  async savePermissions() {
    if (!this.validations()) return;
    this.modulesProfileUserId = await firstValueFrom(this.moduleService.saveModulesByProfileUser(this.profileUserContract.profileUserId, this.modulesProfileUserId));
    this.windowsProfileUser = await firstValueFrom(this.windowService.saveWindowsByProfileUser(this.profileUserContract.profileUserId, this.windowsProfileUser));
    this.toastHandlerService.showToast(
      'success',
      'Los permisos fueron guardados exitosamente'
    );
    this.ref.close();
  }

  private updateExistingWindow(windowProfile: WindowProfileUserModel, value: boolean | null) {
    const index = this.windowsProfileUser.indexOf(windowProfile);

    // elimino permiso
    if (value === null) {
      this.windowsProfileUser.splice(index, 1);
      this.removeModuleIfEmpty(windowProfile.windowId);
      return;
    }

    // actualizar permiso
    windowProfile.updatable = value;
  }

  validations() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para modificar el usuario. Comuníquese con el administrador del sistema'
      );
      return false;
    }
    return true;
  }
}
